/**
 * Cause & Effect (FMEA) API Client
 * Handles all failure mode, root cause, and cascade link API requests
 */

import type {
  CauseEffect,
  CreateCauseEffectDTO,
  UpdateCauseEffectDTO,
  CauseEffectLink,
} from '@fmeastudio/shared'

// Re-export shared types for consumers of this API module
export type { CauseEffect, CauseEffectLink }
import { API_BASE_URL } from './config'

const BASE_URL = `${API_BASE_URL}/cause-effects`

export interface CauseEffectFilters {
  projectId?: string
  type?: 'FAILURE_MODE' | 'ROOT_CAUSE'
  category?: string
  componentId?: string
  search?: string
}

/**
 * RPN history entry returned from the API
 */
export interface RPNHistoryEntry {
  id: string
  causeEffectId: string
  rpn: number
  severity: number
  occurrence: number
  detection: number
  timestamp: string
  createdAt: string
}

/**
 * Cascade link between failure modes
 */
export interface CascadeLink {
  id: string
  sourceId: string
  targetId: string
  strength?: number
  notes?: string
  createdAt: string
  updatedAt: string
  source?: CauseEffect
  target?: CauseEffect
}

/**
 * Component-to-failure mode link
 */
export interface ComponentFailureLink {
  id: string
  componentId: string
  failureModeId: string
  likelihood?: string
  notes?: string
  createdAt: string
  updatedAt: string
}

/**
 * Failure mode to root cause link
 */
export interface FailureCauseLink {
  id: string
  failureModeId: string
  rootCauseId: string
  probability?: number
  notes?: string
  createdAt: string
  updatedAt: string
}

/**
 * Get all cause-effects with optional filtering
 */
export async function getCauseEffects(filters?: CauseEffectFilters): Promise<CauseEffect[]> {
  const params = new URLSearchParams()

  if (filters?.projectId) params.append('projectId', filters.projectId)
  if (filters?.type) params.append('type', filters.type)
  if (filters?.category) params.append('category', filters.category)
  if (filters?.componentId) params.append('componentId', filters.componentId)
  if (filters?.search) params.append('search', filters.search)

  const url = params.toString() ? `${BASE_URL}?${params}` : BASE_URL
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error('Failed to fetch cause-effects')
  }

  const data = await response.json()
  return data.data || []
}

/**
 * Get a single cause-effect by ID
 */
export async function getCauseEffect(id: string): Promise<CauseEffect> {
  const response = await fetch(`${BASE_URL}/${id}`)

  if (!response.ok) {
    throw new Error('Failed to fetch cause-effect')
  }

  const data = await response.json()
  return data.data
}

/**
 * Map frontend CauseEffect DTO field names to the backend schema field names.
 * Backend schema (createCauseEffectSchema / updateCauseEffectSchema) uses
 * `category` (not `category5m`) and `mitigationStrategy` (not `mitigation`),
 * and has no `title`/`potentialConsequences` fields. stripUnknown drops
 * unrecognised keys, so we only send the fields the schema accepts.
 */
function toCauseEffectPayload(
  data: Partial<CreateCauseEffectDTO> & { projectId?: string }
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  if (data.type !== undefined) payload.type = data.type
  if (data.description !== undefined) payload.description = data.description
  if (data.category5m !== undefined && data.category5m !== null)
    payload.category = data.category5m
  if (data.severity !== undefined) payload.severity = data.severity
  if (data.occurrence !== undefined) payload.occurrence = data.occurrence
  if (data.detection !== undefined) payload.detection = data.detection
  if (data.mitigation !== undefined && data.mitigation !== null)
    payload.mitigationStrategy = data.mitigation
  // Optimistic locking: forward the expected version so the backend can detect
  // concurrent edits. stripUnknown on the backend keeps only whitelisted keys,
  // and the update validator now accepts expectedVersion (mirrors FEA-132).
  if (
    'expectedVersion' in data &&
    (data as { expectedVersion?: number }).expectedVersion !== undefined
  )
    payload.expectedVersion = (data as { expectedVersion?: number }).expectedVersion
  return payload
}

/**
 * Create a new cause-effect (failure mode or root cause)
 */
export async function createCauseEffect(
  data: CreateCauseEffectDTO & { projectId: string }
): Promise<CauseEffect> {
  const { projectId } = data

  const response = await fetch(`${BASE_URL}?projectId=${projectId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(toCauseEffectPayload(data)),
  })

  if (!response.ok) {
    throw new Error('Failed to create cause-effect')
  }

  const result = await response.json()
  return result.data
}

/**
 * Update an existing cause-effect
 */
export async function updateCauseEffect(
  id: string,
  data: UpdateCauseEffectDTO
): Promise<CauseEffect> {
  const response = await fetch(`${BASE_URL}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(toCauseEffectPayload(data)),
  })

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Cause-effect not found (404)')
    }
    throw new Error(`Failed to update cause-effect (${response.status})`)
  }

  const result = await response.json()
  return result.data
}

/**
 * Delete a cause-effect (soft delete)
 */
export async function deleteCauseEffect(id: string): Promise<void> {
  const response = await fetch(`${BASE_URL}/${id}`, {
    method: 'DELETE',
  })

  if (!response.ok) {
    throw new Error('Failed to delete cause-effect')
  }
}

/**
 * Get RPN history for a failure mode
 */
export async function getRPNHistory(failureModeId: string): Promise<RPNHistoryEntry[]> {
  const response = await fetch(`${BASE_URL}/${failureModeId}/rpn-history`)

  if (!response.ok) {
    throw new Error('Failed to fetch RPN history')
  }

  const data = await response.json()
  return data.data || []
}

/**
 * Get cascade links for a failure mode
 */
export async function getCascadeLinks(failureModeId: string): Promise<{
  upstream: CascadeLink[]
  downstream: CascadeLink[]
}> {
  const response = await fetch(`${API_BASE_URL}/cascade-links/failure-mode/${failureModeId}`)

  if (!response.ok) {
    throw new Error('Failed to fetch cascade links')
  }

  const data = await response.json()
  return data.data || { upstream: [], downstream: [] }
}

/**
 * Create a cascade link between two failure modes
 */
export async function createCascadeLink(data: {
  sourceId: string
  targetId: string
  strength?: number
  notes?: string
}): Promise<CascadeLink> {
  const response = await fetch(`${API_BASE_URL}/cascade-links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create cascade link')
  }

  const result = await response.json()
  return result.data
}

/**
 * Delete a cascade link
 */
export async function deleteCascadeLink(linkId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/cascade-links/${linkId}`, {
    method: 'DELETE',
  })

  if (!response.ok) {
    throw new Error('Failed to delete cascade link')
  }
}

/**
 * Link a component to a failure mode
 */
export async function linkComponentToFailure(data: {
  componentId: string
  failureModeId: string
  likelihood?: string
  notes?: string
}): Promise<ComponentFailureLink> {
  const response = await fetch(`${API_BASE_URL}/component-failure-links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error('Failed to link component to failure mode')
  }

  const result = await response.json()
  return result.data
}

/**
 * Link a failure mode to a root cause
 */
export async function linkFailureToCause(data: {
  failureModeId: string
  rootCauseId: string
  probability?: number
  notes?: string
}): Promise<FailureCauseLink> {
  const response = await fetch(`${API_BASE_URL}/failure-cause-links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error('Failed to link failure mode to root cause')
  }

  const result = await response.json()
  return result.data
}

/**
 * Get all links for a component
 */
export async function getComponentLinks(componentId: string): Promise<CauseEffectLink[]> {
  const response = await fetch(`${API_BASE_URL}/component-failure-links/component/${componentId}`)

  if (!response.ok) {
    throw new Error('Failed to fetch component links')
  }

  const data = await response.json()
  return data.data || []
}

/**
 * Get all links for a failure mode
 */
export async function getFailureModeLinks(failureModeId: string): Promise<{
  componentLinks: ComponentFailureLink[]
  causeLinks: FailureCauseLink[]
}> {
  const safeFetch = async (url: string) => {
    try {
      const r = await fetch(url)
      if (!r.ok) return { data: [] }
      return r.json()
    } catch {
      return { data: [] }
    }
  }

  const [componentLinks, causeLinks] = await Promise.all([
    safeFetch(`${API_BASE_URL}/component-failure-links/failure/${failureModeId}`),
    safeFetch(`${API_BASE_URL}/failure-cause-links/failure/${failureModeId}`),
  ])

  return {
    componentLinks: componentLinks.data || [],
    causeLinks: causeLinks.data || [],
  }
}

// ============================================================================
// Component Effects & Root Cause Links (BUG-058)
// ============================================================================

export interface ComponentEffect {
  id: string
  componentId: string
  causeEffectId: string
  createdAt: string
  updatedAt: string
}

export interface RootCauseLink {
  id: string
  failureModeId: string
  rootCauseId: string
  createdAt: string
  updatedAt: string
}

/**
 * Create a component effect (link component to cause-effect)
 */
export async function createComponentEffect(data: {
  componentId: string
  causeEffectId: string
}): Promise<ComponentEffect> {
  // Repointed to the mounted component-failure-links alias router.
  // Body shape per createComponentFailureLinkSchema: { componentId, failureModeId }.
  const response = await fetch(`${API_BASE_URL}/component-failure-links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      componentId: data.componentId,
      failureModeId: data.causeEffectId,
    }),
  })

  if (!response.ok) {
    throw new Error('Failed to create component effect')
  }

  const result = await response.json()
  return result.data
}

/**
 * Create a root cause link (link failure mode to root cause)
 */
export async function createRootCauseLink(data: {
  failureModeId: string
  rootCauseId: string
}): Promise<RootCauseLink> {
  // Repointed to the mounted failure-cause-links alias router.
  // Body shape per createFailureCauseLinkSchema: { failureModeId, rootCauseId }.
  const response = await fetch(`${API_BASE_URL}/failure-cause-links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      failureModeId: data.failureModeId,
      rootCauseId: data.rootCauseId,
    }),
  })

  if (!response.ok) {
    throw new Error('Failed to create root cause link')
  }

  const result = await response.json()
  return result.data
}

// ============================================================================
// Project-scoped Cause Effects (BUG-058)
// ============================================================================

/**
 * Get cause effects for a specific project
 */
export async function getCauseEffectsForProject(projectId: string): Promise<CauseEffect[]> {
  // Repointed to the mounted list route: GET /api/cause-effects?projectId=
  // Envelope: { success, data, total, limit, offset }
  const response = await fetch(`${API_BASE_URL}/cause-effects?projectId=${projectId}`)

  if (!response.ok) {
    throw new Error('Failed to fetch cause effects')
  }

  const data = await response.json()
  return data.data || []
}

// ============================================================================
// Mitigations by Phase (BUG-058)
// ============================================================================

export interface MitigationOnTimeline {
  id: string
  description: string
  phase: string
  status: string
  actionType: string
  rpnBefore?: number | null
  rpnAfter?: number | null
  causeEffectId?: string
  causeEffectTitle?: string
  linkedRiskCount?: number
}

/**
 * Get mitigations grouped by phase, optionally filtered by risk
 */
export async function getMitigationsByPhase(riskId?: string): Promise<MitigationOnTimeline[]> {
  const url = riskId
    ? `${API_BASE_URL}/external-risks/${riskId}/mitigations`
    : `${API_BASE_URL}/cause-effects/mitigations?includePhase=true`

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error('Failed to fetch mitigations')
  }
  const result = await response.json()
  return result.data || []
}
